/*
 * Ricarica automatica (polling) di una tabella finché in pagina ci sono righe
 * in lavorazione.
 *
 * Il componente che la usa deve definire:
 *  - hasActiveRows(): true se qualche riga in pagina cambierà a breve
 * e può ridefinire:
 *  - silentFetch(): fetch in background (niente loader né snackbar) che
 *    rifiuta la promise in caso di errore; di default fetch(true) di
 *    template_lisaweb
 *  - isFirstPage(): di default currentPage === 1
 *  - rowSignature(item): ciò che, se cambia, riporta l'intervallo a 5s; di
 *    default [id, status]
 *  - msUntilActiveRows(): ms dopo i quali una riga in pagina diventerà attiva
 *    (es. uno Schedulato che entra nella finestra), o null; di default null
 */

// intervallo del polling: parte da 5s e raddoppia fino a 30s finché la pagina
// non cambia; torna a 5s appena cambia qualcosa
const POLLING_MIN_MS = 5000;
const POLLING_MAX_MS = 30000;
// dopo tanti errori consecutivi il polling si ferma (riparte con un fetch manuale)
const POLLING_MAX_FAILURES = 3;
// senza righe attive si attende quella che lo diventerà, ricontrollando almeno ogni ora
const POLLING_MIN_WAKE_MS = 1000;
const POLLING_MAX_WAKE_MS = 60 * 60 * 1000;

export default {
  data() {
    return {
      pollingTimer: null, // setTimeout per l'aggiornamento dinamico delle righe in lavorazione
      pollingIntervalMs: POLLING_MIN_MS,
      pollingFailures: 0, // fetch silenziosi falliti consecutivi
      isPollingInProgress: false, // evita tick di polling sovrapposti
      pollingRestartPending: false, // fetch manuale arrivato durante un tick
      isPollingDestroyed: false, // componente distrutto: nessun nuovo tick
    };
  },
  methods: {
    silentFetch() {
      return this.fetch(true);
    },
    isFirstPage() {
      return this.currentPage === 1;
    },
    rowSignature(item) {
      return [item.id, item.status];
    },
    msUntilActiveRows() {
      return null;
    },
    canPoll() {
      // solo in prima pagina: nelle successive le righe scorrono all'arrivo di nuove righe
      return this.isFirstPage() && this.hasActiveRows();
    },
    itemsSignature() {
      return JSON.stringify(this.items.map((item) => this.rowSignature(item)));
    },
    pollActiveRows() {
      this.pollingTimer = null;
      if (this.isPollingDestroyed) return;
      // Le righe attive sono finite durante l'attesa: resta da fissare il
      // risveglio per quelle future.
      if (!this.canPoll()) {
        this.schedulePoll();
        return;
      }
      // scheda in background: in pausa, riprende su visibilitychange
      if (document.hidden) return;
      // non sovrapporsi a un fetch completo né a un tick precedente
      if (this.isLoading || this.isPollingInProgress) {
        this.schedulePoll();
        return;
      }
      const before = this.itemsSignature();
      this.isPollingInProgress = true;
      this.silentFetch()
        .then(() => {
          this.pollingFailures = 0;
          this.pollingIntervalMs =
            this.itemsSignature() !== before
              ? POLLING_MIN_MS
              : Math.min(this.pollingIntervalMs * 2, POLLING_MAX_MS);
          this.schedulePoll();
        })
        .catch((error) => {
          // 401/403: inutile riprovare, il polling si ferma subito
          this.pollingFailures = [401, 403].includes(error?.response?.status)
            ? POLLING_MAX_FAILURES
            : this.pollingFailures + 1;
          if (this.pollingFailures >= POLLING_MAX_FAILURES) {
            this.stopPolling();
            return;
          }
          this.schedulePoll();
        })
        .finally(() => {
          this.isPollingInProgress = false;
          // un fetch manuale (Cerca, Ricarica) arrivato durante il tick
          // riparte da zero: 5s e contatore errori azzerato
          if (this.pollingRestartPending) {
            this.pollingRestartPending = false;
            this.startPolling();
          }
        });
    },
    schedulePoll() {
      this.stopPolling();
      if (this.isPollingDestroyed) return;
      if (this.canPoll()) {
        this.pollingTimer = setTimeout(
          this.pollActiveRows,
          this.pollingIntervalMs,
        );
        return;
      }
      // nessuna riga attiva adesso: un solo timer che si sveglia quando la
      // prima riga lo diventerà e rivaluta
      const wakeMs = this.isFirstPage() ? this.msUntilActiveRows() : null;
      if (wakeMs === null || wakeMs === undefined) return;
      this.pollingTimer = setTimeout(
        this.startPolling,
        Math.min(Math.max(wakeMs, POLLING_MIN_WAKE_MS), POLLING_MAX_WAKE_MS),
      );
    },
    startPolling() {
      this.pollingIntervalMs = POLLING_MIN_MS;
      this.pollingFailures = 0;
      this.schedulePoll();
    },
    // da chiamare prima di un fetch manuale (Cerca, Ricarica): se arriva durante
    // un tick, il watcher su items lo ignora e a ripartire è il tick stesso
    requestPollingRestart() {
      if (this.isPollingInProgress) this.pollingRestartPending = true;
    },
    stopPolling() {
      if (this.pollingTimer) {
        clearTimeout(this.pollingTimer);
        this.pollingTimer = null;
      }
    },
    onVisibilityChange() {
      if (!document.hidden && !this.pollingTimer && !this.isPollingInProgress) {
        // riprende dopo la pausa (non dopo uno stop per errori)
        if (this.pollingFailures < POLLING_MAX_FAILURES) this.startPolling();
      }
    },
  },
  watch: {
    items() {
      // i risultati del polling li gestisce il tick stesso; ogni altro fetch
      // (ricerca, pagina, Ricarica) riparte da zero o ferma il polling
      if (this.isPollingInProgress) return;
      this.startPolling();
    },
  },
  mounted() {
    document.addEventListener("visibilitychange", this.onVisibilityChange);
  },
  beforeDestroy() {
    this.isPollingDestroyed = true;
    this.stopPolling();
    document.removeEventListener("visibilitychange", this.onVisibilityChange);
  },
};
